// Fase 2 «Voci della bolletta»: quali voci mostrare e la riga della striscia del telefono. Funzioni pure.
import type { Segnalazione } from "@/api/analisiBolletta";
import { contaVoci, VOCI_IN_CORSO_BREVE } from "@/lib/analisiScritte";
import type { VoceAnalisi } from "@/components/analisi/useAnalisiChat";

export type Mostrato = "G" | "N" | "V" | "P";
const ORDINE: Mostrato[] = ["G", "N", "V", "P"];      // «V» con la gravità di «N» (21/10)

export type Segnalata = Segnalazione & { giudizio: Mostrato };
/** Una riga stampata in bolletta con i suoi giudizi: la quota potenza può essere gonfiata e fuori posto insieme. */
export interface RigaVoce { prima: Segnalata; altre: Segnalata[] }

/** Stessa riga stampata = stesso nome e stesso valore (lo stesso conto della finestra «Analisi della bolletta»). */
const chiaveRiga = (s: Segnalazione) => `${s.nome ?? s.dicitura}|${s.valore}`;

/** Le voci da mostrare, nell'ordine dei giudizi: solo quelle da segnalare, come il conto della striscia (prova QR del
 *  18/10: «2 voci da segnalare» sopra e tre righe sotto). «In regola» e «da verificare» restano nella finestra.
 *  Una riga per voce stampata (bolletta MET del 18/10: «1 voce» sopra, la quota potenza due volte sotto). */
export function vociDaMostrare(segnalazioni: Segnalazione[] | undefined): RigaVoce[] {
  const righe = new Map<string, RigaVoce>();
  (segnalazioni ?? [])
    .filter((s): s is Segnalata => ORDINE.includes(s.giudizio as Mostrato))
    .sort((a, b) => ORDINE.indexOf(a.giudizio) - ORDINE.indexOf(b.giudizio))
    .forEach((s) => {
      const r = righe.get(chiaveRiga(s));
      if (r) r.altre.push(s);
      else righe.set(chiaveRiga(s), { prima: s, altre: [] });
    });
  return [...righe.values()];
}

/** Riga della striscia sul telefono: lavoro in corso o quante voci. Null se non c'è niente da dire. */
export function sintesiVoci(voce: VoceAnalisi | undefined): { testo: string; inCorso: boolean } | null {
  if (!voce?.dettaglioStato) return null;
  if (voce.dettaglioStato === "in_corso") return { testo: VOCI_IN_CORSO_BREVE, inCorso: true };
  if (voce.dettaglioStato !== "pronta" || voce.dettaglio?.motivo) return null;
  // lo stesso conto della finestra «Analisi della bolletta» (prova QR del 18/10: «3 voci» qui, «2 da segnalare» là):
  // gonfiate, potrebbero non esserci, non dovrebbero esserci, una per riga stampata; le «da verificare» non contano
  const n = voce.dettaglio?.conteggi?.da_segnalare ?? vociDaMostrare(voce.dettaglio?.segnalazioni).length;
  return n > 0 ? { testo: contaVoci(n), inCorso: false } : null;
}
